import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";

// Roboto (mindsquare-Hausschrift) — selbst gehostet via fontsource.
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// Globale Ausnahmen jenseits des Mantine-Themes (aktuell: Druck-Layout).
import "./global.css";

// i18n must be initialized before any component renders
import "./lib/i18n.js";

// dayjs-Locale registrieren, damit DatePicker auf Deutsch rendert.
import "dayjs/locale/de.js";

import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications, notifications } from "@mantine/notifications";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";

import { App } from "./App.js";
import { ApiError } from "./lib/api.js";
import { i18n } from "./lib/i18n.js";
import { theme } from "./theme.js";

// Mutations dürfen sich per meta aus dem globalen Erfolgs-Toast ausklinken
// (z. B. Login/Logout — dort ist der Seitenwechsel das Feedback).
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      silent?: boolean;
    };
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Antworten im 4xx-Bereich sind Aussagen, keine Störungen: „darfst du
      // nicht", „gibt es nicht". Sie dreimal zu wiederholen verzögert die
      // Meldung nur (Befund G2 — der Berechtigungsfall stand nach drei
      // Fehlversuchen als „Daten konnten nicht geladen werden" da). 5xx und
      // Netzfehler dürfen weiterhin einen zweiten Versuch bekommen.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
  // Globales Speicher-Feedback: jede erfolgreiche Mutation zeigt einen kurzen
  // Toast. Fehler bleiben bewusst lokal (Alerts an den Formularen).
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      notifications.show({
        message: i18n.t("common.savedToast"),
        color: "green",
        autoClose: 2000,
        withBorder: true,
      });
    },
  }),
});


ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <MantineProvider defaultColorScheme="light" theme={theme}>
      <DatesProvider settings={{ locale: "de", firstDayOfWeek: 1 }}>
        <Notifications position="top-right" limit={3} />
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </DatesProvider>
    </MantineProvider>
  </React.StrictMode>,
);
